import React, { useState, useEffect } from 'react';
import { CheckOutlined } from '@ant-design/icons';
import './StepsContainer.css';

/**
 * A reusable Steps Container component for multi-step flows
 * 
 * @param {Array} steps - Array of step titles (strings)
 * @param {Function} renderStep - Function that returns JSX based on currentStep index
 * @param {number} initialStep - Initial step index (default: 0)
 * @param {Function} onStepChange - Optional callback when step changes (receives newStep, direction)
 * @param {string} layout - "horizontal" | "vertical" (default: "horizontal")
 * @param {string|Array<string>} imageUrl - Optional image URL (string) or array of URLs (one per step)
 * @param {React.ReactNode} imageContent - Optional custom content for image section
 * @param {boolean} showImage - Whether to show image section (default: true)
 * @param {string} imagePosition - "left" | "right" (default: "left")
 * @param {boolean} showNavButtons - Whether to show navigation buttons (default: true)
 * @param {string} nextButtonText - Custom text for next button
 * @param {string} prevButtonText - Custom text for previous button
 * @param {string} finishButtonText - Custom text for finish button (last step)
 * @param {Function} onFinish - Callback when finish button is clicked on last step
 * @param {boolean} disableNextButton - Externally disable next button
 */
const StepsContainer = ({
  steps = [],
  renderStep,
  initialStep = 0,
  onStepChange,
  layout = 'horizontal',
  imageUrl,
  imageContent,
  showImage = true,
  imagePosition = 'left',
  showNavButtons = true,
  nextButtonText = 'Next',
  prevButtonText = 'Back',
  finishButtonText = 'Finish',
  onFinish,
  disableNextButton = false,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [animationDirection, setAnimationDirection] = useState('next');

  useEffect(() => {
    setCurrentStep(initialStep);
  }, [initialStep]);

  const nextStep = () => {
    if (currentStep < steps.length - 1) {
      const newStep = currentStep + 1;
      setAnimationDirection('next');
      setCurrentStep(newStep);
      onStepChange?.(newStep, 'next');
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      const newStep = currentStep - 1;
      setAnimationDirection('prev');
      setCurrentStep(newStep);
      onStepChange?.(newStep, 'prev');
    }
  };

  const goToStep = (stepIndex) => {
    if (stepIndex >= 0 && stepIndex < steps.length && stepIndex <= currentStep) {
      const direction = stepIndex > currentStep ? 'next' : 'prev';
      setAnimationDirection(direction);
      setCurrentStep(stepIndex);
      onStepChange?.(stepIndex, direction);
    }
  };

  const handleNextClick = () => {
    if (currentStep === steps.length - 1) {
      onFinish?.();
    } else {
      nextStep();
    }
  };

  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === steps.length - 1;

  const renderStepIndicator = () => (
    <div className={`steps-indicator steps-indicator-${layout}`}>
      {steps.map((step, index) => {
        const isCompleted = index < currentStep;
        const isActive = index === currentStep;
        const isClickable = index <= currentStep;

        return (
          <React.Fragment key={index}>
            <div
              className={`step-item ${isCompleted ? 'completed' : ''} ${isActive ? 'active' : ''} ${isClickable ? 'clickable' : ''}`}
              onClick={() => isClickable && goToStep(index)}
            >
              <div className="step-circle">
                {isCompleted ? (
                  <CheckOutlined className="check-icon" />
                ) : (
                  <span className="step-number">{index + 1}</span>
                )}
              </div>
              <span className="step-title">{step}</span>
            </div>
            {index < steps.length - 1 && (
              <div className={`step-connector ${index < currentStep ? 'completed' : ''}`}>
                <div className="connector-line" />
              </div>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );

  const renderImageSection = () => {
    if (!showImage) return null;

    // Support array of images (one per step) or single image
    const currentImageUrl = Array.isArray(imageUrl) ? imageUrl[currentStep] : imageUrl;

    return (
      <div className="steps-image-section">
        {imageContent ? (
          imageContent
        ) : currentImageUrl ? (
          <img src={currentImageUrl} alt="Step illustration" className="steps-image" />
        ) : (
          <div className="steps-image-placeholder">
            <div className="placeholder-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="8.5" cy="8.5" r="1.5" />
                <path d="M21 15l-5-5L5 21" />
              </svg>
            </div>
            <span className="placeholder-text">Add your image here</span>
          </div>
        )}
      </div>
    );
  };

  const renderContent = () => (
    <div className={`steps-content-wrapper animation-${animationDirection}`} key={currentStep}>
      {renderStep?.(currentStep, { nextStep, prevStep, goToStep, isFirstStep, isLastStep })}
    </div>
  );

  const renderNavButtons = () => {
    if (!showNavButtons) return null;

    return (
      <div className="steps-nav-buttons">
        <button
          className="steps-btn steps-btn-secondary"
          onClick={prevStep}
          disabled={isFirstStep}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          {prevButtonText}
        </button>
        <button
          className="steps-btn steps-btn-primary"
          onClick={handleNextClick}
          disabled={disableNextButton}
        >
          {isLastStep ? finishButtonText : nextButtonText}
          {!isLastStep && (
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="btn-icon">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          )}
        </button>
      </div>
    );
  };

  return (
    <div className={`steps-container layout-${layout}`}>
      {/* Step Indicator */}
      <div className="steps-header">
        {renderStepIndicator()}
      </div>

      {/* Main Content Area */}
      <div className={`steps-main ${imagePosition === 'right' ? 'image-right' : 'image-left'}`}>
        {/* Image Section */}
        {imagePosition === 'left' && renderImageSection()}

        {/* Content Section */}
        <div className="steps-content-section">
          <div className="steps-content">
            {renderContent()}
          </div>
          {renderNavButtons()}
        </div>

        {/* Image Section (right position) */}
        {imagePosition === 'right' && renderImageSection()}
      </div>
    </div>
  );
};

export default StepsContainer;
